import config from '../config';
import axios from 'axios';

const apiCall = async (endpoint, options = {}, asText = false) => {
  const token = localStorage.getItem('token');
  
  // 토큰이 없으면 에러 발생
  if (!token) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  
  const response = await fetch(`${config.BACKEND_URL}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const { detail } = errorData;
    // FastAPI 입력 검증 오류는 detail 이 [{loc, msg}] 배열로 온다
    const message = Array.isArray(detail)
      ? detail.map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join(' / ')
      : (typeof detail === 'string' ? detail : null);
    const error = new Error(message || `API 호출 실패: ${response.status}`);
    error.detail = detail;
    throw error;
  }

  return asText ? response.text() : response.json();
};

// 같은 주소의 GET 이 아직 응답을 기다리는 중이면 요청을 새로 보내지 않고 그 응답을 함께 쓴다.
// (화면이 열릴 때 여러 컴포넌트가 같은 목록을 동시에 부르거나, 개발 서버의 StrictMode 가 효과를 두 번 돌리는 경우)
// 응답 본문(문자열)만 함께 쓰고 해석은 호출마다 따로 해서, 한쪽이 결과를 고쳐도 다른 쪽에 번지지 않는다.
const inflightGets = new Map();

export const apiGet = async (endpoint) => {
  const key = `${localStorage.getItem('token')} ${endpoint}`;
  let pending = inflightGets.get(key);
  if (!pending) {
    pending = apiCall(endpoint, { method: 'GET' }, true).finally(() => inflightGets.delete(key));
    inflightGets.set(key, pending);
  }
  return JSON.parse(await pending);
};

export const apiPost = (endpoint, data) => 
  apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiPut = (endpoint, data) => 
  apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const apiDelete = (endpoint) => 
  apiCall(endpoint, { method: 'DELETE' });

export const apiUpload = async (endpoint, formData) => {
  const token = localStorage.getItem('token');
  
  // 토큰이 없으면 에러 발생
  if (!token) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  
  const response = await fetch(`${config.BACKEND_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `파일 업로드 실패: ${response.status}`);
  }

  return response.json();
};

// axios 기반 API 호출 함수들 (AuthContext에서 사용)
const inflightAxiosGets = new Map();

// 같은 주소를 동시에 부르면(로그인 확인이 겹칠 때) 요청 하나를 함께 쓴다
export const apiAxiosGet = (endpoint, options) => {
  if (options) return axiosGet(endpoint, options);
  const key = `${localStorage.getItem('token')} ${endpoint}`;
  let pending = inflightAxiosGets.get(key);
  if (!pending) {
    pending = axiosGet(endpoint).finally(() => inflightAxiosGets.delete(key));
    inflightAxiosGets.set(key, pending);
  }
  return pending;
};

const axiosGet = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  // 토큰이 없으면 에러 발생
  if (!token) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  
  try {
    const response = await axios.get(`${config.BACKEND_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
      ...options,
    });
    
    return response.data;
  } catch (error) {
    // 401 에러인 경우 재로그인 필요
    if (error.response?.status === 401) {
      throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
    }
    throw error;
  }
};

export const apiAxiosPost = async (endpoint, data = {}, options = {}) => {
  const token = localStorage.getItem('token');
  
  // 토큰이 없으면 에러 발생
  if (!token) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  
  try {
    const response = await axios.post(`${config.BACKEND_URL}${endpoint}`, data, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
      ...options,
    });
    
    return response.data;
  } catch (error) {
    // 401 에러인 경우 재로그인 필요
    if (error.response?.status === 401) {
      throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
    }
    throw error;
  }
};

// 로그인 전용 API 호출 함수 (토큰 없이 호출 가능)
export const apiAxiosPostLogin = async (endpoint, data = {}, options = {}) => {
  const response = await axios.post(`${config.BACKEND_URL}${endpoint}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
    ...options,
  });
  
  return response.data;
};

// 파일 다운로드 함수
export const downloadFile = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  
  try {
    // endpoint에 이미 쿼리스트링이 있을 수 있으므로 구분자를 맞춘다
    const separator = endpoint.includes('?') ? '&' : '?';
    const response = await fetch(`${config.BACKEND_URL}${endpoint}${separator}v=${new Date().toISOString().split('T')[0]}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `파일 다운로드 실패: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    throw error;
  }
};